import { useCallback, useEffect, useRef, useState } from 'react';

export default function useDashboardInfiniteScroll({ total, batchSize, resetKey, active = true }) {
    const [windowSize, setWindowSize] = useState({ key: resetKey, count: batchSize });
    const count = windowSize.key === resetKey ? windowSize.count : batchSize;
    const visibleCount = Math.min(total, count);
    const hasMore = visibleCount < total;
    const [sentinel, setSentinel] = useState(null);
    const current = useRef(null);
    current.current = { total, batchSize, resetKey, active };
    const sentinelRef = useCallback(node => setSentinel(node), []);
    const loadMore = useCallback(() => {
        const latest = current.current;
        if (!latest.active) return;
        setWindowSize(previous => {
            const prior = previous.key === latest.resetKey ? previous.count : latest.batchSize;
            return { key: latest.resetKey, count: Math.min(latest.total, prior + latest.batchSize) };
        });
    }, []);
    useEffect(() => { setWindowSize(previous => previous.key === resetKey ? previous : { key: resetKey, count: batchSize }); }, [resetKey, batchSize]);
    useEffect(() => {
        if (!active || !hasMore || !sentinel || typeof IntersectionObserver === 'undefined') return;
        let disposed = false, consumed = false;
        const observer = new IntersectionObserver(entries => {
            if (!disposed && !consumed && entries.some(entry => entry.isIntersecting)) {
                consumed = true;
                loadMore();
            }
        }, { root: null, rootMargin: '0px 0px 160px 0px', threshold: 0 });
        observer.observe(sentinel);
        return () => { disposed = true; observer.disconnect(); };
    }, [active, hasMore, sentinel, visibleCount, resetKey, loadMore]);
    return { visibleCount, sentinelRef, hasMore, loadMore };
}
